import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as messageService from '../../services/messageService';
import { API_BASE_URL } from '../../config/api';
import './MobileArchivePage.css';

const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http')) return avatar;
  if (avatar.startsWith('/uploads')) return avatar;
  return `${API_BASE_URL}${avatar}`;
};

const MobileArchivePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const contextRef = useRef(null);
  const longPressTimer = useRef(null);
  const longPressActivated = useRef(false);

  const fetchArchived = useCallback(async (silent = false) => {
    if (!user?.id) return;
    try {
      if (!silent) setLoading(true);
      const data = await messageService.getConversations(user.id, { archived: true });
      setConversations(Array.isArray(data) ? data : []);
    } catch (_) {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchArchived(); }, [fetchArchived]);

  useEffect(() => {
    if (!contextMenu) return;
    const handler = (e) => {
      if (contextRef.current && !contextRef.current.contains(e.target)) setContextMenu(null);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [contextMenu]);

  const handleUnarchive = async (convId) => {
    setContextMenu(null);
    try {
      await messageService.archiveConversation(convId, false);
      setConversations(prev => prev.filter(c => c.id !== convId));
    } catch (_) {}
  };

  const handleLongPressStart = (convId, e) => {
    const touch = e.touches?.[0] || e;
    const x = touch.clientX;
    const y = touch.clientY;
    longPressTimer.current = setTimeout(() => {
      longPressActivated.current = true;
      setContextMenu({ convId, x, y });
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };

  const filtered = conversations.filter(c => {
    if (!searchQuery) return true;
    const name = c.sender?.name || '';
    const preview = c.preview || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      preview.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <>
      <div className="ma-page">
        <div className="ma-header">
          <button className="ma-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="ma-title">Archived chats</h1>
        </div>

        <div className="ma-search-wrap">
          <div className="ma-search-bar">
            <svg className="ma-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="ma-search-input"
              type="text"
              placeholder="Search archived chats"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="ma-body">
          {loading ? (
            <div className="ma-loading">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="ma-skel">
                  <div className="ma-skel-avatar" />
                  <div className="ma-skel-lines">
                    <div className="ma-skel-line ma-skel-name" />
                    <div className="ma-skel-line ma-skel-preview" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="ma-empty">
              <div className="ma-empty-icon">
                <svg viewBox="0 0 80 80" fill="none">
                  <rect x="12" y="16" width="56" height="12" rx="3" fill="#e0e0e0" />
                  <path d="M16 28v28a4 4 0 004 4h40a4 4 0 004-4V28" stroke="#ccc" strokeWidth="2" fill="#f5f5f5" />
                  <path d="M32 44h16" stroke="#ccc" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </div>
              <p className="ma-empty-text">No archived chats for now</p>
              <p className="ma-empty-sub">Long-press any chat to archive it</p>
            </div>
          ) : (
            <div className="ma-list">
              {filtered.map(conv => {
                const name = conv.sender?.name || 'Unknown';
                const avatarUrl = getAvatarUrl(conv.sender?.avatar);
                const initial = name.charAt(0).toUpperCase();
                return (
                  <button
                    key={conv.id}
                    className="ma-conv-item"
                    type="button"
                    onClick={() => {
                      if (longPressActivated.current) { longPressActivated.current = false; return; }
                      navigate(`/mobile/chat/${conv.id}`);
                    }}
                    onMouseDown={e => handleLongPressStart(conv.id, e)}
                    onMouseUp={handleLongPressEnd}
                    onMouseLeave={handleLongPressEnd}
                    onTouchStart={e => { e.currentTarget.style.userSelect = 'none'; handleLongPressStart(conv.id, e); }}
                    onTouchEnd={handleLongPressEnd}
                    onTouchCancel={handleLongPressEnd}
                  >
                    <div className="ma-conv-avatar">
                      {avatarUrl
                        ? <img src={avatarUrl} alt={name} />
                        : <span>{initial}</span>
                      }
                      <div className="ma-archive-badge" aria-hidden="true">
                        <svg viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="1.5">
                          <rect x="1" y="1" width="10" height="3.5" rx="0.5" />
                          <path d="M2 4.5v5.5a0.5 0.5 0 00.5.5h7a0.5 0.5 0 00.5-.5V4.5" />
                          <path d="M4.5 7h3" />
                        </svg>
                      </div>
                    </div>
                    <div className="ma-conv-info">
                      <div className="ma-conv-name">{name}</div>
                      <div className="ma-conv-last">{conv.preview || 'No messages yet'}</div>
                    </div>
                    <div className="ma-conv-meta">
                      <span className="ma-conv-time">{conv.time || ''}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {contextMenu && (
        <div
          ref={contextRef}
          className="ma-context-menu"
          style={{
            top: Math.min(contextMenu.y, window.innerHeight - 120),
            left: Math.min(contextMenu.x, window.innerWidth - 180)
          }}
        >
          <button
            className="ma-context-item"
            type="button"
            onClick={() => { setContextMenu(null); navigate(`/mobile/chat/${contextMenu.convId}`); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            Open chat
          </button>
          <button
            className="ma-context-item ma-context-unarchive"
            type="button"
            onClick={() => handleUnarchive(contextMenu.convId)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="2" y="3" width="20" height="5" rx="1" />
              <path d="M4 8v11a2 2 0 002 2h12a2 2 0 002-2V8" />
              <path d="M10 12h4" />
            </svg>
            Unarchive
          </button>
        </div>
      )}
    </>
  );
};

export default MobileArchivePage;
